import { useEffect, useMemo, useState } from "react";

import { fetchRegionalProtocolTaskBoard } from "../utils/backend/pdxBackendClient";
import { QUESTIONNAIRES } from "../utils/constants";
import { getReviewMetadataPath, getRecordingModeDestination } from "../utils/navigation/recordingModeNav";
import { RECORDING_MODES } from "../utils/recordingModes";

const QUESTIONNAIRE_META = Object.fromEntries(
  QUESTIONNAIRES.map(({ slug, labelKey }) => [
    slug,
    {
      labelKey,
      icon: "assignment",
    },
  ]),
);

const RECORDING_META = {
  [RECORDING_MODES.SPIROMETRY]: {
    labelKey: "levelSelection.spirometryTitle",
    icon: "air",
    variant: "RecordingSpirometry",
  },
  [RECORDING_MODES.TIDAL]: {
    labelKey: "levelSelection.tidalBreathingTitle",
    icon: "monitor_heart",
    variant: "RecordingTidalBreathing",
  },
  [RECORDING_MODES.PULSE]: {
    labelKey: "levelSelection.pulseScanTitle",
    icon: "ecg",
    variant: "RecordingPulseScan",
  },
  [RECORDING_MODES.MAUDIO]: {
    labelKey: "levelSelection.manualAudioTitle",
    icon: "mic",
    variant: "RecordingManualAudio",
  },
  [RECORDING_MODES.POSTEXERTION]: {
    labelKey: "levelSelection.postExertionTitle",
    icon: "directions_run",
    variant: "RecordingPostExertion",
  },
  [RECORDING_MODES.SIX_MINUTE_WALK]: {
    labelKey: "levelSelection.sixMinuteWalkTitle",
    icon: "directions_walk",
    variant: "RecordingSixMinuteWalk",
  },
};

const RECORDING_TASK_ID_BY_VARIANT = Object.fromEntries(
  Object.entries(RECORDING_META).map(([taskId, meta]) => [meta.variant, taskId]),
);

/**
 * Maps a recording variant ("RecordingSpirometry") back to the protocol task id
 * ("spirometry"). The recording pod reports completions by variant, while the
 * task board is keyed by mode.
 *
 * @param {string} variant
 * @returns {string|null}
 */
export function getRecordingTaskIdForVariant(variant) {
  return RECORDING_TASK_ID_BY_VARIANT[variant] ?? null;
}

function enrichTask(task, firstRunByVariant) {
  const meta =
    task.kind === "recording"
      ? RECORDING_META[task.taskId]
      : QUESTIONNAIRE_META[task.taskId];

  const href =
    task.kind === "recording"
      ? getReviewMetadataPath(
          getRecordingModeDestination(task.taskId, firstRunByVariant),
          task.taskId,
        )
      : `/questionnaires/${task.taskId}`;

  return {
    ...task,
    id: `${task.kind}:${task.taskId}`,
    labelKey: meta?.labelKey ?? null,
    fallbackLabel: task.taskId,
    icon: meta?.icon ?? "checklist",
    href,
  };
}

function createEmptySummary() {
  return {
    assignedTasksCount: 0,
    completedTasksCount: 0,
    dueTasksCount: 0,
    overdueTasksCount: 0,
    unassignedTasksCount: 0,
  };
}

function normalizeContextMetadata(value) {
  const source = value && typeof value === "object" ? value : {};

  return {
    createdAt: source.createdAt || null,
    updatedAt: source.updatedAt || null,
    lastLoginAt: source.lastLoginAt || null,
  };
}

function normalizeScheduleAnchorAt(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  return value ?? null;
}

function resolveScheduleAnchorAt(board, accountCreatedAt = null) {
  const normalizedAccountCreatedAt = normalizeScheduleAnchorAt(accountCreatedAt);

  return (
    normalizedAccountCreatedAt ||
    board?.contextMetadata?.createdAt ||
    board?.scheduleAnchorAt ||
    board?.protocolStartAt ||
    board?.protocolStartAtMs ||
    null
  );
}

function createBaseBoard(state = "no-protocol", scheduleAnchorAt = null) {
  return {
    state,
    tasks: [],
    recordingTasks: [],
    questionnaireTasks: [],
    unassignedTasks: [],
    summary: createEmptySummary(),
    contextMetadata: normalizeContextMetadata(),
    scheduleAnchorAt: normalizeScheduleAnchorAt(scheduleAnchorAt),
  };
}

function normalizeTaskList(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeTaskBoard(board, contextMetadata = null) {
  const normalizedSummary =
    board?.summary && typeof board.summary === "object"
      ? {
          assignedTasksCount: Number(board.summary.assignedTasksCount) || 0,
          completedTasksCount: Number(board.summary.completedTasksCount) || 0,
          dueTasksCount: Number(board.summary.dueTasksCount) || 0,
          overdueTasksCount: Number(board.summary.overdueTasksCount) || 0,
          unassignedTasksCount: Number(board.summary.unassignedTasksCount) || 0,
        }
      : createEmptySummary();
  const normalizedContextMetadata = normalizeContextMetadata(
    board?.contextMetadata || contextMetadata,
  );

  return {
    state: typeof board?.state === "string" ? board.state : "no-protocol",
    tasks: normalizeTaskList(board?.tasks),
    recordingTasks: normalizeTaskList(board?.recordingTasks),
    questionnaireTasks: normalizeTaskList(board?.questionnaireTasks),
    unassignedTasks: normalizeTaskList(board?.unassignedTasks),
    summary: normalizedSummary,
    contextMetadata: normalizedContextMetadata,
    scheduleAnchorAt:
      board?.scheduleAnchorAt ||
      board?.protocolStartAt ||
      board?.protocolStartAtMs ||
      normalizedContextMetadata.createdAt ||
      null,
  };
}

export function useProtocolTaskBoard({
  uid,
  firstRunByVariant = {},
  loading = false,
  refreshKey = 0,
  accountCreatedAt = null,
}) {
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        uid: uid || null,
        refreshKey,
      }),
    [uid, refreshKey],
  );
  const [state, setState] = useState({
    loading: false,
    error: null,
    board: null,
  });

  useEffect(() => {
    if (!uid) {
      setState({
        loading: false,
        error: null,
        board: null,
      });
      return;
    }

    if (loading) {
      setState((previousState) => ({
        ...previousState,
        loading: true,
        error: null,
      }));
      return;
    }

    let cancelled = false;
    setState((previousState) => ({
      ...previousState,
      loading: true,
      error: null,
    }));

    fetchRegionalProtocolTaskBoard()
      .then((response) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          error: null,
          board: normalizeTaskBoard(
            response?.taskBoard,
            response?.contextMetadata,
          ),
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setState({
          loading: false,
          error,
          board: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    requestKey,
    uid,
  ]);

  return useMemo(() => {
    if (loading || state.loading) {
      return {
        loading: true,
        ...createBaseBoard("loading", accountCreatedAt),
        error: null,
      };
    }

    if (state.error) {
      return {
        loading: false,
        ...createBaseBoard("error", accountCreatedAt),
        error: state.error,
      };
    }

    const board = state.board || createBaseBoard("no-protocol", accountCreatedAt);
    const scheduleAnchorAt = resolveScheduleAnchorAt(board, accountCreatedAt);

    return {
      ...board,
      scheduleAnchorAt,
      loading: false,
      error: null,
      tasks: board.tasks.map((task) => enrichTask(task, firstRunByVariant)),
      recordingTasks: board.recordingTasks.map((task) =>
        enrichTask(task, firstRunByVariant),
      ),
      questionnaireTasks: board.questionnaireTasks.map((task) =>
        enrichTask(task, firstRunByVariant),
      ),
      unassignedTasks: board.unassignedTasks.map((task) =>
        enrichTask(task, firstRunByVariant),
      ),
    };
  }, [
    accountCreatedAt,
    firstRunByVariant,
    loading,
    state.board,
    state.error,
    state.loading,
  ]);
}

export default useProtocolTaskBoard;
